/**
 * Endpoint contracts — typed sort + filter declarations mirroring backend
 * @SortableFields / @Filter annotations. Hand-maintained; parity enforced by
 * `RequireEndpointContractTypescriptMatch` in the quality gate.
 *
 * See docs/specs/endpoint-contracts/architecture.md §4.5–§4.6.
 *
 * When adding or editing an endpoint export, the convention check's error
 * message prints the expected shape — copy it in verbatim.
 */

import type {EndpointContract} from './endpoint-contract'

// ============================================================================
// party/
// ============================================================================

export const PartyControllerPartiesEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        PARTY_TYPE: 'partyType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        // parentPartyId (UUID, @Filter(adminOnly = true)) is intentionally
        // omitted from the shared TS contract — the convention check
        // `RequireEndpointContractTypescriptMatch` rejects admin-only filters
        // in the shared export since the TS is shipped to every client.
        // Admin callers still hit the endpoint via URL params directly; the
        // UnifiedSearchBar panel never surfaces the filter because it only
        // renders descriptors present in the contract.
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        PARTY_TYPE: {name: 'partyType', kind: 'csv', element: 'enum', typeTable: 'party_types', displayName: 'Party Type'},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        UPDATED_AT_FROM: {name: 'updatedAtFrom', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'from'},
        UPDATED_AT_TO: {name: 'updatedAtTo', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const PartyRelationshipSchemaControllerListEndpoint = {
    sort: {
        FROM_PARTY_TYPE: 'fromPartyType',
        RELATIONSHIP_TYPE: 'relationshipType',
        TO_PARTY_TYPE: 'toPartyType',
        ACTIVE: 'active',
        SORT_ORDER: 'sortOrder',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        FROM_PARTY_TYPE: {name: 'fromPartyType', kind: 'string', displayName: 'From Party Type'},
        RELATIONSHIP_TYPE: {name: 'relationshipType', kind: 'string', displayName: 'Relationship Type'},
        TO_PARTY_TYPE: {name: 'toPartyType', kind: 'string', displayName: 'To Party Type'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
    },
} as const satisfies EndpointContract

export const PartyRelationshipSchemaControllerViolationsEndpoint = {
    sort: {
        VIOLATION_TYPE: 'violationType',
        CREATED_AT: 'createdAt',
    },
    filter: {
        VIOLATION_TYPE: {name: 'violationType', kind: 'csv', element: 'enum', typeTable: 'PartyRelationshipSchemaViolationType', displayName: 'Violation Type'},
    },
} as const satisfies EndpointContract

// ============================================================================
// identity/
// ============================================================================

export const IdentityControllerIdentitiesEndpoint = {
    sort: {
        PARTY_DISPLAY_NAME: 'party.derivedDisplayName',
        DISPLAY_NAME: 'derivedDisplayName',
        IDENTITY_TYPE: 'identityType',
        LIFECYCLE_STATE: 'lifecycleState',
        ENTRY_DATE: 'entryDate',
        EXIT_DATE: 'exitDate',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        // Multi-select enums via the @Filter(kind=CSV, element=ENUM) backend
        // contract. typeTable values come from AppConfig.typeValuesByName —
        // identity_types is the lookup table; IdentityLifecycleState is the
        // static-enum class simple name. The TS export carries no enum values
        // — labels and codes both flow from the server.
        IDENTITY_TYPE: {name: 'identityType', kind: 'csv', element: 'enum', typeTable: 'identity_types', displayName: 'Identity Type'},
        LIFECYCLE_STATE: {name: 'lifecycleState', kind: 'csv', element: 'enum', typeTable: 'IdentityLifecycleState', displayName: 'Lifecycle State'},
        PARTY_ID: {name: 'partyId', kind: 'uuid', displayName: 'Party', lookup: {endpoint: 'apiInternalParties', labelField: 'derivedDisplayName', searchParam: 'q'}},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        UPDATED_AT_FROM: {name: 'updatedAtFrom', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'from'},
        UPDATED_AT_TO: {name: 'updatedAtTo', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// accessprofileassignment/ (identity-scoped timeline)
// ============================================================================

export const IdentityTimelineControllerTimelineEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract

// WHY: Phase 3 of issue 525 — picker / lookup-combobox source endpoints that
// return a thin Page<EntityLink> projection (id, displayName, entityType,
// subType). One per backing module — drives the Combobox on every
// kind:'uuid' filter without needing a separate detail() call.
export const IdentityControllerLinksEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const IdentityControllerLifecycleEventsEndpoint = {
    sort: {
        CHANGED_AT: 'changedAt',
        TRIGGER_SOURCE: 'triggerSource',
    },
    filter: {
        FROM: {name: 'from', kind: 'datetime', displayName: 'From'},
        TO: {name: 'to', kind: 'datetime', displayName: 'To'},
        TRIGGER_SOURCE: {name: 'triggerSource', kind: 'csv', element: 'enum', displayName: 'Trigger Source'},
    },
} as const satisfies EndpointContract

export const AccountControllerStatusEventsEndpoint = {
    sort: {
        CHANGED_AT: 'changedAt',
        TRIGGER_SOURCE: 'triggerSource',
    },
    filter: {
        FROM: {name: 'from', kind: 'datetime', displayName: 'From'},
        TO: {name: 'to', kind: 'datetime', displayName: 'To'},
        TRIGGER_SOURCE: {name: 'triggerSource', kind: 'csv', element: 'enum', displayName: 'Trigger Source'},
    },
} as const satisfies EndpointContract

export const CrossCuttingLifecycleEventsControllerListEndpoint = {
    sort: {
        CHANGED_AT: 'changedAt',
        TRIGGER_SOURCE: 'triggerSource',
    },
    filter: {
        FROM: {name: 'from', kind: 'datetime', displayName: 'From'},
        TO: {name: 'to', kind: 'datetime', displayName: 'To'},
        TRIGGER_SOURCE: {name: 'triggerSource', kind: 'csv', element: 'enum', displayName: 'Trigger Source'},
        ENTITY: {name: 'entity', kind: 'enum', values: ['IDENTITY', 'ACCOUNT'] as const, displayName: 'Entity Type', typeTable: 'EntityType'},
    },
} as const satisfies EndpointContract

export const TargetSystemControllerLinksEndpoint = {
    sort: {
        DERIVED_DISPLAY_NAME: 'derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const ResourceControllerLinksEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const EnforcementObjectControllerLinksEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const AccessProfileControllerLinksEndpoint = {
    sort: {
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const UserControllerLinksEndpoint = {
    sort: {
        NAME: 'name',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

export const ReportCategoryControllerLinksEndpoint = {
    sort: {
        NAME: 'name',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

// WHY: federated picker dispatcher — backs `apiInternalEntityLinks`. Optional
// `?type=` narrows to one EntityLinkProvider; absent → fans across every
// provider the caller has authority for. Used by ErrorLog.sourceId.
export const EntityLinkControllerLinksEndpoint = {
    sort: {
    },
    filter: {
        TYPE: {name: 'type', kind: 'csv', element: 'enum', typeTable: 'EntityType', displayName: 'Type'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ID: {name: 'id', kind: 'csv', element: 'uuid', displayName: 'Id'},
    },
} as const satisfies EndpointContract

// ============================================================================
// my/ (authenticated-user-scoped timeline)
// ============================================================================

export const MyAccessControllerMyTimelineEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract

// 549 — FG timeline parity. Mirrors MyTimeline above (no sort, no filters);
// the controller still annotates @SortableFields(value = {}) so the
// RequireEndpointContractOnPaginatedEndpoints gate passes.
export const MyAccessControllerMyFunctionGroupTimelineEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract

// 549 Phase 5 — caller-scoped MyAccess (AP) unified search + filter. Multi-select
// AccessProfileAssignmentStatus, validFrom/validTo date range, free-text q on
// the LocalizedString access-profile name. Identity scoping is implicit (caller).
export const MyAccessControllerMyAccessEndpoint = {
    sort: {
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AccessProfileAssignmentStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        VALID_FROM: {name: 'validFrom', kind: 'datetime', displayName: 'Valid From'},
        VALID_TO: {name: 'validTo', kind: 'datetime', displayName: 'Valid To'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// 549 Phase 5 — caller-scoped MyAccess (FG) unified search + filter. Mirrors the
// AP variant above but with FunctionGroupAssignmentStatus and a plain-String
// function-group name (no LocalizedString JSONB).
export const MyAccessControllerMyFunctionGroupAccessEndpoint = {
    sort: {
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'FunctionGroupAssignmentStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        VALID_FROM: {name: 'validFrom', kind: 'datetime', displayName: 'Valid From'},
        VALID_TO: {name: 'validTo', kind: 'datetime', displayName: 'Valid To'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Phase C.7 (issue 342.12) — caller-scoped EffectiveAccess unified search.
// The row-level permission predicate (identity.id = caller) is composed
// OUTERMOST per spec SEC-03 — `q` and filters can never widen the result
// beyond the caller's own grants. Free-text `q` walks one hop through
// @ManyToOne to match the @Searchable display-name fields on Identity,
// TargetSystem, EnforcementObject, and Resource. EffectiveAccess is
// `@ExpectedVolume(LARGE)` but caller-scoping pre-narrows the working set
// to one identity, so the audit-class 3-character min-q from §4.8.3 is
// intentionally NOT enforced — there is no `qMinChars` here. Caller-scoped
// pages do not surface a "user" filter; identityId is implicit (the caller).
export const EffectiveAccessControllerMyEffectiveAccessEndpoint = {
    sort: {
        VALID_FROM: 'validFrom',
        VALID_TO: 'validTo',
        OPERATION: 'operation',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        TARGET_SYSTEM_DERIVED_DISPLAY_NAME: 'targetSystem.derivedDisplayName',
        ENFORCEMENT_OBJECT_DISPLAY_NAME: 'enforcementObject.derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        TARGET_SYSTEM_ID: {name: 'targetSystemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        ENFORCEMENT_OBJECT_ID: {name: 'enforcementObjectId', kind: 'uuid', displayName: 'Enforcement Object', lookup: {endpoint: 'apiInternalEnforcementObjectsLinks', searchParam: 'q', entityType: 'ENFORCEMENT_OBJECT'}},
        RESOURCE_ID: {name: 'resourceId', kind: 'uuid', displayName: 'Resource', lookup: {endpoint: 'apiInternalResourcesLinks', searchParam: 'q', entityType: 'RESOURCE'}},
        VALID_FROM: {name: 'validFrom', kind: 'datetime', displayName: 'Valid From'},
        VALID_TO: {name: 'validTo', kind: 'datetime', displayName: 'Valid To'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// targetsystem/
// ============================================================================

// WHY: Phase C.4 (issue 342.09): Target Systems unified search + typed filters.
// `q` matches the system @Searchable fields (name + description). Multi-select
// systemType / environment sourced from system_types / environment_types via
// AppConfig.typeValuesByName — no hardcoded SelectItem values.
export const TargetSystemControllerSystemsEndpoint = {
    sort: {
        DERIVED_DISPLAY_NAME: 'derivedDisplayName',
        SYSTEM_TYPE: 'systemType',
        ENVIRONMENT: 'environment',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        SYSTEM_TYPE: {name: 'systemType', kind: 'csv', element: 'enum', typeTable: 'system_types', displayName: 'System Type'},
        ENVIRONMENT: {name: 'environment', kind: 'csv', element: 'enum', typeTable: 'environment_types', displayName: 'Environment'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// resource/
// ============================================================================

export const ResourceControllerResourcesEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        RESOURCE_TYPE: 'resourceType',
        DATA_CLASSIFICATION: 'dataClassification',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        SYSTEM_ID: {name: 'systemId', kind: 'uuid', displayName: 'System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        RESOURCE_TYPE: {name: 'resourceType', kind: 'csv', element: 'enum', typeTable: 'resource_types', displayName: 'Resource Type'},
        CLASSIFICATION: {name: 'classification', kind: 'csv', element: 'enum', typeTable: 'data_classification_types', displayName: 'Classification'},
        PARENT_RESOURCE_ID: {name: 'parentResourceId', kind: 'uuid', displayName: 'Parent Resource', lookup: {endpoint: 'apiInternalResourcesLinks', searchParam: 'q', entityType: 'RESOURCE'}},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const ResourceControllerResourcesBySystemEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        RESOURCE_TYPE: 'resourceType',
        DATA_CLASSIFICATION: 'dataClassification',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// enforcementobject/
// ============================================================================

// WHY: Phase C.4 (issue 342.09): Enforcement Objects unified search + typed filters.
// `q` matches the EnforcementObject @Searchable displayName. Opaque external
// identifiers (externalIdentifier) are NOT @Searchable per spec §4.1. Multi-select
// enforcementType sourced from the `enforcement_types` lookup table.
export const EnforcementObjectControllerEnforcementObjectsEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        ENFORCEMENT_TYPE: 'enforcementType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        SYSTEM_ID: {name: 'systemId', kind: 'uuid', displayName: 'System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        RESOURCE_ID: {name: 'resourceId', kind: 'uuid', displayName: 'Resource', lookup: {endpoint: 'apiInternalResourcesLinks', searchParam: 'q', entityType: 'RESOURCE'}},
        ENFORCEMENT_TYPE: {name: 'enforcementType', kind: 'csv', element: 'enum', typeTable: 'enforcement_types', displayName: 'Enforcement Type'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const EnforcementObjectControllerBySystemEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        ENFORCEMENT_TYPE: 'enforcementType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const EnforcementObjectControllerByResourceEndpoint = {
    sort: {
        DISPLAY_NAME: 'derivedDisplayName',
        ENFORCEMENT_TYPE: 'enforcementType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// access/ — effective access + provisioning tasks
// ============================================================================

export const EffectiveAccessControllerGrantHistoryForIdentityEndpoint = {
    sort: {
        RECORD_START: 'recordStart',
        RECORD_END: 'recordEnd',
        VALID_FROM: 'validFrom',
        VALID_TO: 'validTo',
        OPERATION: 'operation',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const EffectiveAccessControllerGrantsForTargetSystemEndpoint = {
    sort: {
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
        ENFORCEMENT_OBJECT_DISPLAY_NAME: 'enforcementObject.derivedDisplayName',
        OPERATION: 'operation',
        VALID_FROM: 'validFrom',
        VALID_TO: 'validTo',
    },
    filter: {
    },
} as const satisfies EndpointContract

// Phase C.6 (issue 342.11) — unified search + typed filters; `q` walks one hop
// through @ManyToOne to match @Searchable identity.derivedDisplayName, targetSystem.name,
// and enforcementObject.derivedDisplayName. Status is CSV-of-enum (multi-select);
// targetSystemId / identityId are entity references rendered via Combobox per
// .claude/rules/frontend.md (large sets must not preload into Select).
export const ProvisioningTaskControllerTasksEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_DERIVED_DISPLAY_NAME: 'identity.derivedDisplayName',
        TARGET_SYSTEM_DERIVED_DISPLAY_NAME: 'targetSystem.derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'ProvisioningTaskStatus', displayName: 'Status'},
        TARGET_SYSTEM_ID: {name: 'targetSystemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        IDENTITY_ID: {name: 'identityId', kind: 'uuid', displayName: 'Identity', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        ACCOUNT_ID: {name: 'accountId', kind: 'uuid', displayName: 'Account', lookup: {endpoint: 'apiInternalAccounts', labelField: 'identityDisplayName', descriptionField: 'externalIdentifier', searchParam: 'q'}},
        ENFORCEMENT_OBJECT_ID: {name: 'enforcementObjectId', kind: 'uuid', displayName: 'Enforcement Object', lookup: {endpoint: 'apiInternalEnforcementObjectsLinks', searchParam: 'q', entityType: 'ENFORCEMENT_OBJECT'}},
        CREATED_SINCE: {name: 'createdSince', kind: 'datetime', displayName: 'Created Since'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// De-verb: getTasksByIdentity → TasksByIdentity
export const ProvisioningTaskControllerTasksByIdentityEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        ACTION: 'action',
        STATUS: 'status',
        TARGET_SYSTEM_DERIVED_DISPLAY_NAME: 'targetSystem.derivedDisplayName',
    },
    filter: {
    },
} as const satisfies EndpointContract

// De-verb: getTasksByTargetSystem → TasksByTargetSystem
export const ProvisioningTaskControllerTasksByTargetSystemEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
    },
    filter: {
    },
} as const satisfies EndpointContract

// De-verb: searchHistory → SearchHistory.
// ProvisioningTaskHistory is the audit-class exception page in spec §8.5 — HUGE
// volume, minimum 3-character q (§4.8.3) enforced server-side (HTTP 400 +
// VA0001_MIN_LENGTH below threshold). The error column is intentionally NOT
// @Searchable; only the human-readable display-name fields participate in q
// (identityDisplayName, targetSystemName, enforcementObjectDisplayName).
// The EndpointContract shape does not yet model `qMinChars`, so the page owns
// the inline 3-char hint (same pattern as ErrorLogControllerAllEndpoint).
export const ProvisioningTaskControllerSearchHistoryEndpoint = {
    sort: {
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_DISPLAY_NAME: 'identityDisplayName',
        TARGET_SYSTEM_NAME: 'targetSystemName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'ProvisioningTaskStatus', displayName: 'Status'},
        TARGET_SYSTEM_ID: {name: 'targetSystemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        IDENTITY_ID: {name: 'identityId', kind: 'uuid', displayName: 'Identity', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        EXECUTED_FROM: {name: 'executedFrom', kind: 'datetime', displayName: 'Executed From'},
        EXECUTED_TO: {name: 'executedTo', kind: 'datetime', displayName: 'Executed To'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// De-verb: getHistoryByIdentity → HistoryByIdentity
export const ProvisioningTaskControllerHistoryByIdentityEndpoint = {
    sort: {
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
        ACTION: 'action',
        STATUS: 'status',
        TARGET_SYSTEM_NAME: 'targetSystemName',
    },
    filter: {
    },
} as const satisfies EndpointContract

// De-verb: getHistoryByTargetSystem → HistoryByTargetSystem
export const ProvisioningTaskControllerHistoryByTargetSystemEndpoint = {
    sort: {
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_DISPLAY_NAME: 'identityDisplayName',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// autoassign/ — auto-assign rules + executions (de-verb collision resolved via
// @SortableFields(name = "AllExecutions" / "RuleExecutions") per spec §4.5)
// ============================================================================

export const AutoAssignRuleControllerRulesEndpoint = {
    sort: {
        NAME: 'name',
        PRIORITY: 'priority',
        ACTIVE: 'active',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE_ONLY: {name: 'activeOnly', kind: 'boolean', displayName: 'Active Only'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        UPDATED_AT_FROM: {name: 'updatedAtFrom', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'from'},
        UPDATED_AT_TO: {name: 'updatedAtTo', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const AutoAssignRuleControllerAllExecutionsEndpoint = {
    sort: {
        EXECUTED_AT: 'executedAt',
        DURATION_MS: 'durationMs',
        STATUS: 'status',
        RULE_NAME: 'rule.name',
    },
    filter: {
        // Multi-select via @Filter(kind=CSV, element=ENUM) — typeTable points
        // at AppConfig.typeValuesByName['AutoAssignRuleExecutionStatus'],
        // populated server-side from the AutoAssignRuleExecutionStatus static
        // enum (TypeService.STATIC_ENUMS). Closes issue 269 — no hardcoded
        // SelectItem values in AutoAssignRuleExecutions.tsx.
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AutoAssignRuleExecutionStatus', displayName: 'Status'},
    },
} as const satisfies EndpointContract

export const AutoAssignRuleControllerRuleExecutionsEndpoint = {
    sort: {
        EXECUTED_AT: 'executedAt',
        DURATION_MS: 'durationMs',
        STATUS: 'status',
    },
    filter: {
        // Same auto-inferred ENUM contract as AllExecutions — multi-select
        // sourced from AppConfig.typeValuesByName['AutoAssignRuleExecutionStatus'].
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AutoAssignRuleExecutionStatus', displayName: 'Status'},
    },
} as const satisfies EndpointContract

// ============================================================================
// authorization/ — roles + users (294.05 Wave)
// ============================================================================

// WHY: Phase C.5 (issue 342.10) — unified search + typed filter; `q` matches the
// @Searchable name + description on AuthRole; `systemRole` is a Boolean toggle
// (renders as a tri-state in UnifiedSearchBar; absent → all roles).
// WHY: Issue 429 Part C / 466 follow-up — paginated lookup over the permissions
// registry. Backs the LookupCombobox-driven `permissionIds` filter on the Roles
// page. The endpoint accepts `q` (project-wide convention) even though
// AuthPermission's resource/action columns are GenericTypeKey value objects
// rather than @Searchable Strings — the controller opts the validator out via
// `@SortableFields(customSearchPredicate = true)` because the service emits a
// hand-written `LOWER(...) LIKE` predicate over both columns.
export const AuthPermissionControllerPermissionsEndpoint = {
    sort: {
        RESOURCE: 'resource',
        ACTION: 'action',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
} as const satisfies EndpointContract

export const RoleControllerRolesEndpoint = {
    sort: {
        NAME: 'name',
        DESCRIPTION: 'description',
        SYSTEM_ROLE: 'systemRole',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        SYSTEM_ROLE: {name: 'systemRole', kind: 'boolean', displayName: 'System Role'},
        PERMISSION_IDS: {name: 'permissionIds', kind: 'csv', element: 'uuid', displayName: 'Permissions', lookup: {endpoint: 'apiInternalPermissions', labelField: 'label', searchParam: 'q'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Phase C.5 (issue 342.10) — unified search + typed filter; `q` matches the
// @Searchable name on AuthHumanUser; `active` is a Boolean toggle (absent →
// all human users; true → active only; false → inactive only).
// WHY: Issue 429 pilot — `roleIds` (CSV of role UUIDs) and `identityIds` (CSV of
// identity UUIDs) are multi-select entity-reference filters. The `lookup` block
// drives FilterPanel's generic LookupCombobox: type-ahead against the referenced
// entity's list endpoint, chip per picked UUID, removable, URL CSV round-trip.
export const UserControllerUsersWithRolesEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
        ROLE_IDS: {name: 'roleIds', kind: 'csv', element: 'uuid', displayName: 'Role', lookup: {endpoint: 'apiInternalRoles', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
        IDENTITY_IDS: {name: 'identityIds', kind: 'csv', element: 'uuid', displayName: 'Identity', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        TYPE: {name: 'type', kind: 'enum', values: ['USER', 'SERVICE_ACCOUNT'] as const, displayName: 'Type', typeTable: 'AuthSubjectType'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// accessprofile/ + accessprofileassignment/ (294.05 Wave)
// ============================================================================

// Phase C.2 (issue 342.07): typed filter contract — q-search intentionally
// omitted because AccessProfile.name is a JSONB LocalizedString (no
// @Searchable column surface yet, see 342.07.99 follow-up). The legacy
// `search` parameter remains for the requestable / auto-assignable form
// pickers (RestrictionRule + AutoAssignRule wizards still drive it).
export const AccessProfileControllerProfilesEndpoint = {
    sort: {
        CODE: 'code',
        ENVIRONMENT: 'environment',
        RISK_LEVEL: 'riskLevel',
        STATUS: 'status',
        EXPIRY_REQUIRED: 'expiryRequired',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        SEARCH: {name: 'search', kind: 'string', displayName: 'Search'},
        RESOURCE_ID: {name: 'resourceId', kind: 'uuid', displayName: 'Resource', lookup: {endpoint: 'apiInternalResourcesLinks', searchParam: 'q', entityType: 'RESOURCE'}},
        ENVIRONMENT: {name: 'environment', kind: 'string', displayName: 'Environment'},
        RISK_LEVEL: {name: 'riskLevel', kind: 'string', displayName: 'Risk Level'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AccessProfileStatus', displayName: 'Status'},
        REQUESTABLE: {name: 'requestable', kind: 'boolean', displayName: 'Requestable'},
        AUTO_ASSIGNABLE: {name: 'autoAssignable', kind: 'boolean', displayName: 'Auto-assignable'},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
} as const satisfies EndpointContract

export const AccessProfileControllerByResourceEndpoint = {
    sort: {
        CODE: 'code',
        ENVIRONMENT: 'environment',
        RISK_LEVEL: 'riskLevel',
        STATUS: 'status',
        EXPIRY_REQUIRED: 'expiryRequired',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// functiongroup/ (294.05 Wave)
// ============================================================================

// WHY: Issue 508 — FunctionGroup search→q migration (completes the 452 generalisation).
// `q` matches the @Searchable name plus values stored in function_group_attributes
// (registered via FunctionGroupDynamicAttributeTableRegistration).
export const FunctionGroupControllerListEndpoint = {
    sort: {
        DERIVED_DISPLAY_NAME: 'derivedDisplayName',
        FUNCTION_GROUP_TYPE: 'functionGroupType',
        AUTO_ASSIGNABLE: 'autoAssignable',
        REQUESTABLE: 'requestable',
        EXPIRY_REQUIRED: 'expiryRequired',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        REQUESTABLE: {name: 'requestable', kind: 'boolean', displayName: 'Requestable'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// accessrequest/ (294.05 Wave) — governance
// ============================================================================

export const AccessRequestControllerRequestsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        REQUESTER_IDENTITY_PARTY_DISPLAY_NAME: 'requesterIdentity.party.derivedDisplayName',
        BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME: 'beneficiaryIdentity.party.derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RequestStatus', displayName: 'Status'},
        // createdAt date-range pair (HQG follow-up on 342.04 — same gap as AAR Requests).
        // Mirrors the Parties pattern (§4.3 dateRange grouping).
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        REQUESTER_IDENTITY_ID: {name: 'requesterIdentityId', kind: 'uuid', displayName: 'Requester', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        BENEFICIARY_IDENTITY_ID: {name: 'beneficiaryIdentityId', kind: 'uuid', displayName: 'Beneficiary', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const AccessRequestControllerMyRequestsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME: 'beneficiaryIdentity.party.derivedDisplayName',
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RequestStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        // createdAt date-range pair — mirrors AccessRequestControllerRequestsEndpoint (issue 544).
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        BENEFICIARY_IDENTITY_ID: {name: 'beneficiaryIdentityId', kind: 'uuid', displayName: 'Beneficiary', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        ACCESS_PROFILE_ID: {name: 'accessProfileId', kind: 'uuid', displayName: 'Access Profile', lookup: {endpoint: 'apiInternalAccessProfilesLinks', searchParam: 'q', entityType: 'ACCESS_PROFILE'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const MyDelegationControllerSearchMyDelegationsEndpoint = {
    sort: {
        START_AT: 'startAt',
        END_AT: 'endAt',
        STATUS: 'status',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'DelegationStatus', displayName: 'Status'},
        VALID_FROM: {name: 'validFrom', kind: 'datetime', displayName: 'Valid from'},
        VALID_TO: {name: 'validTo', kind: 'datetime', displayName: 'Valid to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const AccessRequestControllerMyApprovalsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        VALID_FROM: 'validFrom',
        EXPIRES_AT: 'expiresAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RequestItemStatus', displayName: 'Status'},
        ACCESS_PROFILE_ID: {name: 'accessProfileId', kind: 'uuid', displayName: 'Access Profile', lookup: {endpoint: 'apiInternalAccessProfilesLinks', searchParam: 'q', entityType: 'ACCESS_PROFILE'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// autoassignrequest/ (294.05 Wave)
// ============================================================================

export const AutoAssignRuleRequestControllerRequestsEndpoint = {
    sort: {
        NAME: 'name',
        STATUS: 'status',
        REQUEST_TYPE: 'requestType',
        PRIORITY: 'priority',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AutoAssignRuleRequestStatus', displayName: 'Status'},
        RULE_ID: {name: 'ruleId', kind: 'uuid', displayName: 'Rule', lookup: {endpoint: 'apiInternalAutoAssignRules', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const AutoAssignRuleRequestControllerMyApprovalsEndpoint = {
    sort: {
        NAME: 'name',
        STATUS: 'status',
        REQUEST_TYPE: 'requestType',
        PRIORITY: 'priority',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// functiongrouprequest/ (294.05 Wave)
// ============================================================================

export const FunctionGroupRequestControllerRequestsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        REQUESTER_IDENTITY_PARTY_DISPLAY_NAME: 'requesterIdentity.party.derivedDisplayName',
        BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME: 'beneficiaryIdentity.party.derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RequestStatus', displayName: 'Status'},
        // createdAt date-range pair (HQG follow-up on 342.04 — same gap as AAR Requests).
        // Mirrors the Parties pattern (§4.3 dateRange grouping).
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const FunctionGroupRequestControllerMyRequestsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME: 'beneficiaryIdentity.party.derivedDisplayName',
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RequestStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        // createdAt date-range pair — mirrors FunctionGroupRequestControllerRequestsEndpoint (issue 544).
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        BENEFICIARY_IDENTITY_ID: {name: 'beneficiaryIdentityId', kind: 'uuid', displayName: 'Beneficiary', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        FUNCTION_GROUP_ID: {name: 'functionGroupId', kind: 'uuid', displayName: 'Function Group', lookup: {endpoint: 'apiInternalFunctionGroups', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const FunctionGroupRequestControllerMyApprovalsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        VALID_FROM: 'validFrom',
        EXPIRES_AT: 'expiresAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// sodrequest/ (294.05 Wave) — restriction rule requests
// ============================================================================

export const RestrictionRuleRequestControllerRequestsEndpoint = {
    sort: {
        NAME: 'name',
        STATUS: 'status',
        REQUEST_TYPE: 'requestType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RestrictionRuleRequestStatus', displayName: 'Status'},
        RULE_ID: {name: 'ruleId', kind: 'uuid', displayName: 'Rule', lookup: {endpoint: 'apiInternalRestrictionRules', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
        OWNER_PARTY_ID: {name: 'ownerPartyId', kind: 'uuid', displayName: 'Owner', lookup: {endpoint: 'apiInternalParties', labelField: 'derivedDisplayName', searchParam: 'q'}},
        REVIEWED_BY_IDENTITY_ID: {name: 'reviewedByIdentityId', kind: 'uuid', displayName: 'Reviewed By', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const RestrictionRuleRequestControllerMyApprovalsEndpoint = {
    sort: {
        NAME: 'name',
        STATUS: 'status',
        REQUEST_TYPE: 'requestType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// sod/ (294.05 Wave) — restriction rules + violations
// ============================================================================

export const RestrictionRuleControllerRulesEndpoint = {
    sort: {
        NAME: 'name',
        ACTIVE: 'active',
        REMOVAL_GRACE_DAYS: 'removalGraceDays',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE_ONLY: {name: 'activeOnly', kind: 'boolean', displayName: 'Active Only'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const RestrictionRuleControllerViolationsEndpoint = {
    sort: {
        DETECTED_AT: 'detectedAt',
        RESOLVED_AT: 'resolvedAt',
        RULE_NAME: 'rule.name',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
        VIOLATING_PROFILE_CODE: 'violatingProfile.code',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const RestrictionRuleControllerRuleViolationsEndpoint = {
    sort: {
        DETECTED_AT: 'detectedAt',
        RESOLVED_AT: 'resolvedAt',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
        VIOLATING_PROFILE_CODE: 'violatingProfile.code',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const RestrictionRuleControllerDetectionHistoryEndpoint = {
    sort: {
        DETECTION_STARTED_AT: 'detectionStartedAt',
        CREATED_AT: 'createdAt',
        STATUS: 'status',
        IDENTITIES_EVALUATED: 'identitiesEvaluated',
        VIOLATIONS_FOUND: 'violationsFound',
        VIOLATIONS_RESOLVED: 'violationsResolved',
        DURATION_MS: 'durationMs',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const RestrictionRuleControllerAllDetectionsEndpoint = {
    sort: {
        DETECTION_STARTED_AT: 'detectionStartedAt',
        CREATED_AT: 'createdAt',
        STATUS: 'status',
        IDENTITIES_EVALUATED: 'identitiesEvaluated',
        VIOLATIONS_FOUND: 'violationsFound',
        VIOLATIONS_RESOLVED: 'violationsResolved',
        DURATION_MS: 'durationMs',
    },
    filter: {
        RULE_ID: {name: 'ruleId', kind: 'uuid', displayName: 'Rule', lookup: {endpoint: 'apiInternalRestrictionRules', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
    },
} as const satisfies EndpointContract

export const RestrictionRuleControllerVersionHistoryEndpoint = {
    sort: {
        RESTRICTION_RULE_VERSION_RESPONSE__VERSION_NUMBER: 'RestrictionRuleVersionResponse_.versionNumber',
        RESTRICTION_RULE_VERSION_RESPONSE__CREATED_AT: 'RestrictionRuleVersionResponse_.createdAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// account/ (294.05 Wave) — scoped by target system
// ============================================================================

export const AccountControllerAccountsEndpoint = {
    sort: {
        EXTERNAL_IDENTIFIER: 'externalIdentifier',
        STATUS: 'status',
        DISABLED_AT: 'disabledAt',
        DELETED_AT: 'deletedAt',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        // Multi-select via @Filter(kind=CSV, element=ENUM) — typeTable points
        // at AppConfig.typeValuesByName['AccountStatus'], populated server-side
        // from the AccountStatus static enum (AppConfigController).
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'AccountStatus', displayName: 'Status'},
        TARGET_SYSTEM_ID: {name: 'targetSystemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
        SYSTEM_TYPE: {name: 'systemType', kind: 'string', displayName: 'System Type'},
        ENVIRONMENT: {name: 'environment', kind: 'string', displayName: 'Environment'},
        IDENTITY_ID: {name: 'identityId', kind: 'uuid', displayName: 'Identity', lookup: {endpoint: 'apiInternalIdentitiesLinks', searchParam: 'q', entityType: 'IDENTITY'}},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        UPDATED_AT_FROM: {name: 'updatedAtFrom', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'from'},
        UPDATED_AT_TO: {name: 'updatedAtTo', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const AccountControllerAccountsForTargetSystemEndpoint = {
    sort: {
        EXTERNAL_IDENTIFIER: 'externalIdentifier',
        STATUS: 'status',
        DISABLED_AT: 'disabledAt',
        DELETED_AT: 'deletedAt',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
    },
    filter: {
    },
} as const satisfies EndpointContract

// ============================================================================
// certification/ (294.05 Wave)
// ============================================================================

export const CertificationReviewControllerReviewItemsEndpoint = {
    sort: {
        STATUS: 'status',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
        REVIEWER_IDENTITY_PARTY_DISPLAY_NAME: 'reviewerIdentity.party.derivedDisplayName',
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'ReviewItemStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Phase C.3 (issue 342.08): flat reviewer-scoped list page (`/access-governance/
// certification-reviews`). Reviewer-scoping is enforced server-side per SEC-03 —
// the reviewer-scope predicate is the outermost AND so q + filters cannot leak
// another reviewer's rows. Spec §4.8.5.
export const CertificationReviewControllerListEndpoint = {
    sort: {
        STATUS: 'status',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        IDENTITY_DISPLAY_NAME: 'identity.derivedDisplayName',
        REVIEWER_IDENTITY_DISPLAY_NAME: 'reviewerIdentity.derivedDisplayName',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        CAMPAIGN_ID: {name: 'campaignId', kind: 'uuid', displayName: 'Campaign', lookup: {endpoint: 'apiInternalCertificationCampaigns', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
        DECISION: {name: 'decision', kind: 'csv', element: 'enum', typeTable: 'ReviewItemStatus', displayName: 'Status'},
        DUE_BY: {name: 'dueBy', kind: 'date', displayName: 'Due By'},
        RESOURCE_TYPE: {name: 'resourceType', kind: 'csv', element: 'enum', typeTable: 'resource_types', displayName: 'Resource Type'},
        SYSTEM_ID: {name: 'systemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const CertificationReviewControllerMyReviewItemsEndpoint = {
    sort: {
        STATUS: 'status',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        IDENTITY_DISPLAY_NAME: 'identity.derivedDisplayName',
        IDENTITY_PARTY_DISPLAY_NAME: 'identity.party.derivedDisplayName',
    },
    filter: {
        CAMPAIGN_ID: {name: 'campaignId', kind: 'uuid', displayName: 'Campaign', lookup: {endpoint: 'apiInternalCertificationCampaigns', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'ReviewItemStatus', displayName: 'Status'},
        DECISION: {name: 'decision', kind: 'csv', element: 'enum', displayName: 'Decision'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: 465 Bucket C — riskLevel + decisionType are filtered as raw String codes by
// the repository. They render as text inputs in the unified bar's filter panel rather
// than typed selects. Promoting to `kind: 'enum'` with typeTables would require
// either (a) adding CertificationDecisionType to STATIC_ENUMS (translations + DB
// trigger work) or (b) registering the existing risk_level_types via
// typeValuesByName lookup — both out of scope for this UI-primitive swap. Tracked
// in 465.99 as a follow-up.
export const CertificationReviewControllerDecisionsEndpoint = {
    sort: {
    },
    filter: {
        RISK_LEVEL: {name: 'riskLevel', kind: 'string', displayName: 'Risk Level'},
        DECISION_TYPE: {name: 'decisionType', kind: 'string', displayName: 'Decision Type'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Phase C.3 (issue 342.08): unified search + filter contract — `q` against
// CertificationPolicy.name (the only @Searchable field) plus the boolean
// activeOnly narrowing flag, mirroring the AutoAssignRule / RestrictionRule
// shape post-342.07.
export const CertificationPolicyControllerPoliciesEndpoint = {
    sort: {
        NAME: 'name',
        PRIORITY: 'priority',
        ACTIVE: 'active',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE_ONLY: {name: 'activeOnly', kind: 'boolean', displayName: 'Active Only'},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Phase C.3 (issue 342.08): Certification Campaigns unified search + typed
// filters. `q` matches the campaign @Searchable name (one-hop walk also covers
// owner identity displayName + linked policy name via SearchableContractRegistry).
// Multi-select status sourced from AppConfig.typeValuesByName['CertificationCampaignStatus']
// (TypeService.STATIC_ENUMS) — no hardcoded SelectItem values.
export const CertificationCampaignControllerCampaignsEndpoint = {
    sort: {
        NAME: 'name',
        STATUS: 'status',
        DUE_DATE: 'dueDate',
        STARTED_AT: 'startedAt',
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'CertificationCampaignStatus', displayName: 'Status'},
        POLICY_ID: {name: 'policyId', kind: 'uuid', displayName: 'Policy', lookup: {endpoint: 'apiInternalCertificationPolicies', labelField: 'name', descriptionField: 'description', searchParam: 'q'}},
        OWNED_BY_ME: {name: 'ownedByMe', kind: 'boolean', displayName: 'Owned by me'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Phase C.2 (issue 342.07): admin Delegations list — `q` (free-text against
// IdentityDelegation.reason + delegator/deputy party.derivedDisplayName via the
// SearchableContractRegistry 1-hop walk), multi-select `statuses`,
// validAtFrom/validAtTo temporal pair per spec §4.8.1.
export const DelegationControllerAllEndpoint = {
    sort: {
        DELEGATOR_PARTY_DISPLAY_NAME: 'delegator.party.derivedDisplayName',
        DEPUTY_PARTY_DISPLAY_NAME: 'deputy.party.derivedDisplayName',
        STATUS: 'status',
        START_AT: 'startAt',
        END_AT: 'endAt',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        STATUSES: {name: 'statuses', kind: 'csv', element: 'enum', typeTable: 'DelegationStatus', displayName: 'Status'},
        WORKFLOW_STEP_CODE: {name: 'workflowStepCode', kind: 'string', displayName: 'Workflow Step Code'},
        VALID_AT_FROM: {name: 'validAtFrom', kind: 'datetime', displayName: 'Valid At', dateRange: 'validAt', dateRangeBound: 'from'},
        VALID_AT_TO: {name: 'validAtTo', kind: 'datetime', displayName: 'Valid At', dateRange: 'validAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


// WHY: Phase C.6 (issue 342.11) — Request Inspector / Trace List migrated to
// the unified search + filter contract. q matches the @Searchable display
// fields on RequestTraceSpan (operationName, httpUrl); a session is returned
// when at least one of its spans matches. RequestTraceSpan is the audit-class
// exception page in spec §8.5 — HUGE volume; min 3-character q (§4.8.3)
// enforced server-side (HTTP 400 + VA0001_MIN_LENGTH below threshold). The
// diagnostic columns (sqlFingerprint, sqlStatement, errorCode, errorMessage,
// traceId, sessionId, actionId, correlationId, e2eRunId, e2eTestClass,
// e2eTestMethod, attributes, httpMethod) are intentionally NOT @Searchable;
// they remain reachable through the typed exact-match filter inputs. The
// EndpointContract shape does not yet model `qMinChars`, so the page owns
// the inline 3-char hint (same pattern as ProvisioningHistory).
// `user` (auth_user_id, UUID) wires the new apiInternalUsersLinks endpoint
// (issue 525 Phase 3). LookupCombobox now needs only list() — detail() was
// dropped — so the AUTH_USER picker works without UserController growing
// a /users/{id} endpoint.
export const ObservabilityControllerTracesEndpoint = {
    sort: {
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        SESSION: {name: 'session', kind: 'string', displayName: 'Session'},
        USER: {name: 'user', kind: 'uuid', displayName: 'User', lookup: {endpoint: 'apiInternalUsersLinks', searchParam: 'q', entityType: 'AUTH_USER'}},
        E2E_TEST_CLASS: {name: 'e2eTestClass', kind: 'string', displayName: 'E2e Test Class'},
        E2E_TEST_METHOD: {name: 'e2eTestMethod', kind: 'string', displayName: 'E2e Test Method'},
        E2E_RUN_ID: {name: 'e2eRunId', kind: 'string', displayName: 'E2e Run'},
        CORRELATION_ID: {name: 'correlationId', kind: 'string', displayName: 'Correlation'},
        TRACE_ID: {name: 'traceId', kind: 'string', displayName: 'Trace'},
        ACTION_ID: {name: 'actionId', kind: 'string', displayName: 'Action'},
        SINCE: {name: 'since', kind: 'datetime', displayName: 'Since'},
        SLOWER_THAN_MS: {name: 'slowerThanMs', kind: 'number', displayName: 'Slower Than Ms'},
        ERRORS_ONLY: {name: 'errorsOnly', kind: 'boolean', displayName: 'Errors Only'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


export const ObservabilityControllerSessionsEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract


// WHY: Phase C.4 (issue 342.09): unified search + filter contract for Outbound
// Connectors. `q` reaches the connector's `targetSystem.name` (and
// `targetSystem.description`) via the registry's auto-walked @OneToOne
// association — the connector entity carries no direct @Searchable field.
// Opaque external-config fields on the per-type config tables (baseUrl,
// host, jdbcUrl, settings, credential refs, SQL/template content) are
// intentionally NOT searchable per spec §4.1 / issue 332. Multi-select
// connectorType sourced from AppConfig.typeValuesByName['outbound_connector_types']
// (TypeKey-backed).
export const OutboundConnectorControllerConnectorsEndpoint = {
    sort: {
        CONNECTOR_TYPE: 'connectorType',
        ENABLED: 'enabled',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        CONNECTOR_TYPE: {name: 'connectorType', kind: 'csv', element: 'enum', typeTable: 'outbound_connector_types', displayName: 'Connector Type'},
        ENABLED: {name: 'enabled', kind: 'boolean', displayName: 'Enabled'},
        TARGET_SYSTEM_ID: {name: 'targetSystemId', kind: 'uuid', displayName: 'Target System', lookup: {endpoint: 'apiInternalSystemsLinks', searchParam: 'q', entityType: 'SYSTEM'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


export const OutboundConnectorControllerOutboundConnectorLogsEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract

// Per-connector Provisioning Tasks tab (issue 601).
export const OutboundConnectorControllerProvisioningTasksEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_DERIVED_DISPLAY_NAME: 'identity.derivedDisplayName',
        TARGET_SYSTEM_DERIVED_DISPLAY_NAME: 'targetSystem.derivedDisplayName',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const OutboundConnectorControllerProvisioningHistoryEndpoint = {
    sort: {
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
        ACTION: 'action',
        STATUS: 'status',
        IDENTITY_DISPLAY_NAME: 'identityDisplayName',
        TARGET_SYSTEM_NAME: 'targetSystemName',
    },
    filter: {
    },
} as const satisfies EndpointContract


// WHY: Phase C.4 (issue 342.09): unified search + filter contract for Inbound
// Connectors. `q` matches the connector @Searchable fields (displayName +
// description). Opaque external-config fields (baseUrl, queryFilter,
// settings, credential refs) are intentionally NOT searchable per spec
// §4.1 / issue 332. Multi-select connectorType sourced from
// AppConfig.typeValuesByName['inbound_connector_types'] (TypeKey-backed).
export const InboundConnectorControllerConnectorsEndpoint = {
    sort: {
        DISPLAY_NAME: 'displayName',
        CONNECTOR_TYPE: 'connectorType',
        ENABLED: 'enabled',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        CONNECTOR_TYPE: {name: 'connectorType', kind: 'csv', element: 'enum', typeTable: 'inbound_connector_types', displayName: 'Connector Type'},
        ENABLED: {name: 'enabled', kind: 'boolean', displayName: 'Enabled'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


export const InboundSyncControllerSyncRunsEndpoint = {
    sort: {
        STARTED_AT: 'startedAt',
        COMPLETED_AT: 'completedAt',
        CREATED_AT: 'createdAt',
    },
    filter: {
    },
} as const satisfies EndpointContract


export const InboundSyncControllerSyncRecordsEndpoint = {
    sort: {
    },
    filter: {
        ACTION: {name: 'action', kind: 'string', displayName: 'Action'},
    },
} as const satisfies EndpointContract


// WHY: Phase C.5 (issue 342.10): Error Logs is the audit-log-class exception
// page per spec §8.7 — large-volume table with GIN trigram on lower(message).
// `q` matches the only @Searchable field (ErrorLog.message); stackTrace is
// intentionally excluded (too noisy). The minimum 3-character constraint per
// §4.8.3 is enforced by the backend (HTTP 400 + VA0001_MIN_LENGTH below
// threshold) and surfaced by an inline hint on the ErrorLogs page itself —
// the EndpointContract shape does not yet model `minLength`, so the page
// owns the hint.
export const ErrorLogControllerAllEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        LAST_SEEN_AT: 'lastSeenAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        MODULE: {name: 'module', kind: 'string', displayName: 'Module'},
        SOURCE_TYPE: {name: 'sourceType', kind: 'string', displayName: 'Source Type'},
        // WHY: ErrorLog.sourceId is a polymorphic FK — (source_type, source_id) where
        // source_type names which entity type the id points at. `apiInternalLinks`
        // is the federated dispatcher; lookup omits `entityType` so the picker
        // searches across every type the caller has authority for and surfaces
        // mixed EntityLink rows tagged with their own entityType.
        SOURCE_ID: {name: 'sourceId', kind: 'uuid', displayName: 'Source', lookup: {endpoint: 'apiInternalEntityLinks', searchParam: 'q'}},
        CORRELATION_ID: {name: 'correlationId', kind: 'uuid', displayName: 'Correlation'},
        FROM: {name: 'from', kind: 'datetime', displayName: 'From'},
        TO: {name: 'to', kind: 'datetime', displayName: 'To'},
        CONTEXT_KEY: {name: 'contextKey', kind: 'string', displayName: 'Context Key'},
        CONTEXT_VALUE: {name: 'contextValue', kind: 'string', displayName: 'Context Value'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


// WHY: 529.03 — admin-only DDL audit list. Sort surface mirrors the
// backend @SortableFields({"occurredAt", "durationMs", "actorUsername"}).
// Filters are the query params accepted by ScriptExecuteLogController.list.
// HQG follow-up 766 §9: actorUsername (ILIKE) + errorCode (exact) are
// server-side now — the page no longer client-side filters on actorUsername.
export const ScriptExecuteLogControllerListEndpoint = {
    sort: {
        OCCURRED_AT: 'occurredAt',
        DURATION_MS: 'durationMs',
        ACTOR_USERNAME: 'actorUsername',
    },
    filter: {
        ACTOR_USER_ID: {name: 'actorUserId', kind: 'uuid', displayName: 'Actor (UUID)'},
        ACTOR_USERNAME: {name: 'actorUsername', kind: 'string', displayName: 'Actor'},
        MODE: {name: 'mode', kind: 'string', displayName: 'Mode'},
        SUCCESS: {name: 'success', kind: 'boolean', displayName: 'Outcome'},
        ERROR_CODE: {name: 'errorCode', kind: 'string', displayName: 'Error code'},
        OCCURRED_AT_FROM: {name: 'occurredAtFrom', kind: 'datetime', displayName: 'From'},
        OCCURRED_AT_TO: {name: 'occurredAtTo', kind: 'datetime', displayName: 'To'},
    },
} as const satisfies EndpointContract


// WHY: 465.1 Phase 4 — listPatterns now accepts `q` matching the only
// @Searchable field on ErrorLog (message), same semantics as listAll.
// The min-3-char hint per spec §4.8.3 is owned by the page (the
// EndpointContract shape does not yet model `minLength`).
export const ErrorLogControllerPatternsEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        LAST_SEEN_AT: 'lastSeenAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        MODULE: {name: 'module', kind: 'string', displayName: 'Module'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


export const ErrorLogControllerOccurrencesByTemplateEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        LAST_SEEN_AT: 'lastSeenAt',
    },
    filter: {
    },
} as const satisfies EndpointContract


export const JobExecutionControllerAllExecutionsEndpoint = {
    sort: {
        STARTED_AT: 'startedAt',
        COMPLETED_AT: 'completedAt',
        STATUS: 'status',
        DURATION_MS: 'durationMs',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        JOB_ID: {name: 'jobId', kind: 'uuid', displayName: 'Job', lookup: {endpoint: 'apiInternalJobs', labelField: 'name', searchParam: 'q'}},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'JobExecutionStatus', displayName: 'Status'},
        PERIOD: {name: 'period', kind: 'string', displayName: 'Period'},
        FROM: {name: 'from', kind: 'datetime', displayName: 'From', dateRange: 'startedAt', dateRangeBound: 'from'},
        TO: {name: 'to', kind: 'datetime', displayName: 'To', dateRange: 'startedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const JobExecutionControllerExecutionsEndpoint = {
    sort: {
        STARTED_AT: 'startedAt',
        COMPLETED_AT: 'completedAt',
        STATUS: 'status',
        DURATION_MS: 'durationMs',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const ExecutionLogControllerLogsEndpoint = {
    sort: {
    },
    filter: {
    },
} as const satisfies EndpointContract


// WHY: Phase C.6 (issue 342.11) — unified search + typed filters; `q` matches the
// @Searchable name + description on JobDefinition; `enabled` is a Boolean tri-state;
// `source` narrows by JobSource (STATIC / DYNAMIC / TESTDATA).
export const JobDefinitionControllerJobsEndpoint = {
    sort: {
        NAME: 'name',
        JOB_KEY: 'jobKey',
        ENABLED: 'enabled',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ENABLED: {name: 'enabled', kind: 'boolean', displayName: 'Enabled'},
        SOURCE: {name: 'source', kind: 'csv', element: 'enum', typeTable: 'JobSource', displayName: 'Source'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// Issue 509 — ClusterNode is JPA-mapped read-only over `bg_cluster_nodes`,
// so the controller now carries `@SortableFields` and the unified search/sort
// flow applies. `hostname` and `ipAddress` are converter-backed value objects
// (not plain Strings), so the backend uses `customSearchPredicate = true` and
// emits the LIKE predicate by hand — `q` still matches all three columns
// (nodeId, hostname, ipAddress) case-insensitively.
export const ClusterControllerNodesEndpoint = {
    sort: {
        NODE_ID: 'nodeId',
        HOSTNAME: 'hostname',
        IP_ADDRESS: 'ipAddress',
        STATUS: 'status',
        REGISTERED_AT: 'registeredAt',
        LAST_HEARTBEAT: 'lastHeartbeat',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const ReportDefinitionControllerSearchReportsEndpoint = {
    sort: {
        NAME: 'name',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
} as const satisfies EndpointContract

export const ReportDefinitionControllerExecutionsEndpoint = {
    sort: {
        STARTED_AT: 'startedAt',
        COMPLETED_AT: 'completedAt',
        STATUS: 'status',
        DURATION_MS: 'durationMs',
    },
    filter: {
    },
} as const satisfies EndpointContract

// WHY: Phase C.6 (issue 342.11) — Report Categories migrated from the legacy
// `search` RequestParam to the unified `q` surface. q matches the @Searchable
// fields on ReportCategory (name + description). ReportCategory is
// @ExpectedVolume(TRIVIAL); the small-volume LOWER(col) LIKE scan suffices.
// No qMinChars — categories aren't an audit-class page, so the operator can
// narrow with 1-2 characters as well.
export const ReportCategoryControllerCategoriesEndpoint = {
    sort: {
        NAME: 'name',
        CATEGORY_TYPE: 'categoryType',
        DISPLAY_ORDER: 'displayOrder',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        TYPE: {name: 'type', kind: 'csv', element: 'enum', typeTable: 'ReportCategoryType', displayName: 'Type'},
        MODIFIED_AFTER: {name: 'modifiedAfter', kind: 'datetime', displayName: 'Modified After'},
        ROOT_ONLY: {name: 'rootOnly', kind: 'boolean', displayName: 'Root Only'},
        PARENT_ID: {name: 'parentId', kind: 'uuid', displayName: 'Parent', lookup: {endpoint: 'apiInternalReportCategoriesLinks', searchParam: 'q', entityType: 'REPORT_CATEGORY'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Phase C.5 (issue 342.10) — Value Lists unified search + typed filter.
// `q` matches the CustomValueList @Searchable fields (name + displayName +
// description). Boolean `active` filter narrows to active or inactive lists.
// Issue 429 Part C: `hasEntries` boolean filter narrows to lists with at
// least one entry (true) or no entries (false), via EXISTS subquery on
// custom_value_list_entries.
export const ValueListControllerValueListsEndpoint = {
    sort: {
        NAME: 'name',
        DISPLAY_NAME: 'displayName',
        ACTIVE: 'active',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
        HAS_ENTRIES: {name: 'hasEntries', kind: 'boolean', displayName: 'Has entries', booleanLabels: {true: 'Has entries', false: 'No entries'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Phase C.5 (issue 342.10): Form Layouts unified search + typed filter.
// `q` matches the FormLayout @Searchable fields (useCaseKey + displayName).
// Boolean `active` filter narrows to active or inactive layouts.
export const FormLayoutControllerLayoutsEndpoint = {
    sort: {
        USE_CASE_KEY: 'useCaseKey',
        DISPLAY_NAME: 'displayName',
        ACTIVE: 'active',
        UPDATED_AT: 'updatedAt',
        CREATED_AT: 'createdAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Issue 429 — ApprovalPolicies page migrated to UnifiedSearchBar with
// server-side pagination, q-search across @Searchable name+description, multi-
// select policyTypes (CSV<ApprovalPolicyType>), and a boolean active filter.
// ApprovalPolicy carries @ExpectedVolume(TRIVIAL); pagination is a contract-
// parity affordance, not a volume control. Multi-select sourced from
// AppConfig.typeValuesByName['ApprovalPolicyType'] (registered in
// TypeService.STATIC_ENUMS).
export const ApprovalPolicyControllerPoliciesEndpoint = {
    sort: {
        NAME: 'name',
        PRIORITY: 'priority',
        ACTIVE: 'active',
        POLICY_TYPE: 'policyType',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        POLICY_TYPES: {name: 'policyTypes', kind: 'csv', element: 'enum', typeTable: 'ApprovalPolicyType', displayName: 'Policy Type'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
        CREATED_AT_FROM: {name: 'createdAtFrom', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'from'},
        CREATED_AT_TO: {name: 'createdAtTo', kind: 'datetime', displayName: 'Created At', dateRange: 'createdAt', dateRangeBound: 'to'},
        UPDATED_AT_FROM: {name: 'updatedAtFrom', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'from'},
        UPDATED_AT_TO: {name: 'updatedAtTo', kind: 'datetime', displayName: 'Updated At', dateRange: 'updatedAt', dateRangeBound: 'to'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Issue 429 — Types page migrated to UnifiedSearchBar. The endpoint is
// intentionally non-paginated (the type-table registry holds ~25 TRIVIAL-volume
// entries, sorted alphabetically client-side); rootEntity on the Java
// @SortableFields is pinned to TypeTableSummary so the convention check accepts
// the orphan-free contract entry. Empty sort map — the page sorts client-side.
export const TypeControllerTypeTablesEndpoint = {
    sort: {
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        ENTITY_TYPE_ONLY: {name: 'entityTypeOnly', kind: 'boolean', displayName: 'Entity Types Only', booleanLabels: {true: 'Entity types', false: 'Other tables'}},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// WHY: Phase C.5 (issue 342.10): LLM Prompt Templates unified search.
// `q` matches the @Searchable fields (key + displayName + description).
// LlmPromptTemplate carries no enum status field, so no typed filters
// are exposed beyond the free-text search.
export const LlmPromptTemplateControllerListEndpoint = {
    sort: {
        KEY: 'key',
        DISPLAY_NAME: 'displayName',
        DESCRIPTION: 'description',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

// ============================================================================
// jlm/ — rehire review
// ============================================================================

export const RehireReviewControllerListEndpoint = {
    sort: {
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RehireReviewStatus', displayName: 'Status'},
    },
} as const satisfies EndpointContract

export const RehireReviewControllerMineEndpoint = {
    sort: {
    },
    filter: {
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'RehireReviewStatus', displayName: 'Status'},
    },
} as const satisfies EndpointContract

export const NotificationTemplateControllerListEndpoint = {
    sort: {
    },
    filter: {
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
        CUSTOMISED: {name: 'customised', kind: 'boolean', displayName: 'Customised'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
        CATEGORY: {name: 'category', kind: 'csv', element: 'enum', typeTable: 'NotificationTemplateCategory', displayName: 'Category'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const NotificationControllerListEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        SENT_AT: 'sentAt',
        NOTIFICATION_STATUS: 'notificationStatus',
        EVENT_TYPE: 'eventType',
    },
    filter: {
        EVENT_TYPE: {name: 'eventType', kind: 'csv', element: 'enum', typeTable: 'NotificationEventType', displayName: 'Event Type'},
        READ: {name: 'read', kind: 'boolean', displayName: 'Read'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract

export const NotificationControllerLogEndpoint = {
    sort: {
        CREATED_AT: 'createdAt',
        SENT_AT: 'sentAt',
        NOTIFICATION_STATUS: 'notificationStatus',
        EVENT_TYPE: 'eventType',
    },
    filter: {
        EVENT_TYPE: {name: 'eventType', kind: 'csv', element: 'enum', typeTable: 'NotificationEventType', displayName: 'Event Type'},
        STATUS: {name: 'status', kind: 'csv', element: 'enum', typeTable: 'NotificationStatus', displayName: 'Status'},
        Q: {name: 'q', kind: 'string', displayName: 'Search'},
    },
    search: {param: 'q'},
} as const satisfies EndpointContract


export const ConstraintControllerListEndpoint = {
    sort: {
        NAME: 'name',
        TYPE: 'type',
        SEVERITY: 'severity',
        ENABLED: 'enabled',
        CREATED_AT: 'createdAt',
        UPDATED_AT: 'updatedAt',
        LAST_REVIEWED_AT: 'lastReviewedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const ViolationControllerListEndpoint = {
    sort: {
        DETECTED_AT: 'detectedAt',
        SEVERITY: 'severity',
        STATUS: 'status',
        RESOLVED_AT: 'resolvedAt',
    },
    filter: {
    },
} as const satisfies EndpointContract

export const ServiceAccountControllerListEndpoint = {
    sort: {
        NAME: 'name',
        ACTIVE: 'active',
        CREATED_AT: 'createdAt',
    },
    filter: {
        NAME: {name: 'name', kind: 'string', displayName: 'Name'},
        ACTIVE: {name: 'active', kind: 'boolean', displayName: 'Active'},
    },
    search: {param: 'name'},
} as const satisfies EndpointContract
